<template>
  <div v-if="hasPaymentHistory">
    <content-box class="remain-pv--wrap" v-if="pvStatus.length">
      <p class="content__title">{{ $t('userInfo.managePlan.default.pvChart') }}</p>
      <label class="content__label">
        <input type="radio"
               name="chart-type"
               @input="chartType = 'remainPV'"
               :value="'remainPV'"
               v-model="chartType"><i class="icon icon--radio-off"></i>
        {{ $t('common.table.header.remaingPV') }}
      </label>
      <label class="content__label">
        <input type="radio"
               name="chart-type"
               @input="chartType = 'usePV'"
               :value="'usePV'"
               v-model="chartType"><i class="icon icon--radio-off"></i>
        {{ $t('common.table.header.userPV') }}
      </label>
      <div class="date-range-picker--wrap">
        <button
          class="periods_label"
          @click.stop="togglePickerClick"
        >
          {{dateRangeStr}}
        </button>
        <transition name="calendar">
          <date-range-picker
            v-if="showDateRangePicker"
            pickerType="pvHistory"
            :minDate="minDate"
            :maxDate="maxDate"
            :dateRange="dateRange"
            @onRangeCancel="onRangeCancel"
            @onRangeChange="onRangeChange"
          />
        </transition>
        <i class="icon icon--calendar"></i>
      </div>
      <scroll-content>
        <app-chart chartType="line"
                  :chartOptions="chartOptions"
                  :height="190" />
      </scroll-content>
    </content-box>

    <content-box class="remain-pv-table--wrap">
      <div class="table--wrap" ref="listTable">
        <p class="content__title">{{ $t('userInfo.managePlan.default.pvChart') }}</p>
        <ui-selectbox
          class="some-class"
          :value="duration"
          :items="durationList"
          :width="'175px'"
          @input="onDurationChange"
        />

        <app-pagination
          class="clearfix"
          :total="pvStatusCount"
          :page="page"
          :limit="limit"
          @onPagingOptionChange="onPagingOptionChange" />

        <data-table class="pv-detail"
          :headers="headers">
          <template v-slot:colgroup>
            <colgroup>
              <col width="25%">
              <col width="25%">
              <col width="25%">
              <col width="25%">
            </colgroup>
          </template>
          <template v-if="pvStatus.length" v-slot>
            <tbody>
            <tr v-for="(item, index) in pvStatusList" :key="index">
              <td class="data-date">{{ item.date | dashDate }}</td>
              <td class="data-remain">{{ item.remainPV | thousand }}</td>
              <td class="data-used">{{ item.usePV | thousand }}</td>
              <td class="data-provide">{{ item.chargePV | thousand }}</td>
            </tr>
            </tbody>
          </template>
          <tr v-else>
            <td class="no-data" colspan="6">
              <p v-html="$t('common.noRecord')"></p>
            </td>
          </tr>
        </data-table>
      </div>
    </content-box>
  </div>
</template>

<script>
import ContentBox from '@/components/ui/ContentBox'
import DateRangePicker from '@/components/date-picker/DateRangePicker'
import AppPagination from '@/components/ui/AppPagination'
import UiSelectbox from '@/components/ui/UiSelectbox'
import DataTable from '@/components/ui/DataTable'

import { mapState } from 'vuex'
import { parse, format, startOfDay, addDays, addMonths } from 'date-fns'
import { EventBus } from '@/libs/event-bus'
import { getPvStatus } from '@/api/user'
import { getTooltipHtml } from '@/libs/chart-helper'
import { thousand, getLocaleDateStr } from '@/libs/utils'
import AppChart from '@/components/chart'
import ScrollContent from '@/components/ui/ScrollContent'

export default {
  components: {
    ContentBox,
    DateRangePicker,
    AppChart,
    ScrollContent,
    AppPagination,
    UiSelectbox,
    DataTable
  },
  props: [
    'startDate'
  ],
  data () {
    return {
      isLoading: true,
      hasPaymentHistory: false,
      minDate: null,
      maxDate: null,
      dateRange: {
        startDate: null,
        endDate: null
      },
      showDateRangePicker: false,
      // pv table
      headers: [
        {
          name: this.$t('common.table.header.date')
        },
        {
          name: this.$t('common.table.header.remaingPV')
        },
        {
          name: this.$t('common.table.header.userPV')
        },
        {
          name: this.$t('common.table.header.providedPV')
        }
      ],
      page: 1,
      limit: 30,
      pvStatus: [],
      chartType: 'remainPV',
      duration: 0,
      durationList: [
        {
          value: 0,
          text: this.$t('common.all')
        },
        {
          value: 3,
          text: this.$t('common.recentMonths', { month: '3' })
        },
        {
          value: 6,
          text: this.$t('common.recentMonths', { month: '6' })
        },
        {
          value: 12,
          text: this.$t('common.recentMonths', { month: '12' })
        },
        {
          value: 24,
          text: this.$t('common.recentMonths', { month: '24' })
        }
      ]
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData'
    ]),
    pvStatusCount () {
      return this.pvStatus ? this.pvStatus.length : 0
    },
    pvStatusList () {
      if (!this.pvStatus) {
        return []
      }
      const startIndex = (this.page - 1) * this.limit
      const endIndex = startIndex + this.limit
      return this.pvStatus.slice(startIndex, endIndex)
    },
    dateRangeStr () {
      return `${getLocaleDateStr(this.dateRange.startDate)} - ${getLocaleDateStr(this.dateRange.endDate)}`
    },
    chartOptions () {
      const pvData = this.pvStatus.map(item => {
        return [
          format(parse(item.date), 'YYYY-MM-DD'),
          item[this.chartType] > 0 ? item[this.chartType] : 0
        ]
      })

      const options = {
        meta: {
          isDailyChart: true
        },
        grid: {
          left: 10,
          right: 35
        },
        series: [{
          name: 'PV',
          type: 'line',
          data: pvData,
          showSymbol: false,
          symbolSize: 2,
          smooth: true
        }],
        tooltip: {
          formatter (params) {
            const item = params[0]
            const title = format(parse(item.axisValue), 'YYYY-MM-DD')
            let seriesName = item.seriesName
            let value = item.value[1]
            const content = [
              [
                `${item.marker}${seriesName} : `,
                thousand(value)
              ]
            ]
            return getTooltipHtml(content, title)
          }
        },
        xAxis: {
          // type: 'category',
          boundaryGap: false,
          axisLabel: {
            formatter (value) {
              return format(parse(value), 'YYYY-MM-DD')
            }
          }
        },
        yAxis: {
          type: 'value',
          splitNumber: 2,
          axisLabel: {}
        },
        color: '#EC0047'
      }
      return options
    }
  },
  created () {
    const today = startOfDay(new Date())
    const minDate = startOfDay(parse(this.userData.regDate))
    const maxDate = parse(today)
    const endDate = parse(today)
    let startDate = addDays(today, -6)

    if (startDate < minDate) {
      startDate = parse(minDate)
    }

    this.hasPaymentHistory = true
    this.minDate = minDate
    this.maxDate = maxDate

    this.dateRange = {
      startDate,
      endDate
    }
    this.loadData()
  },
  mounted () {
    EventBus.$on('closeDateRangePicker', () => {
      this.showDateRangePicker = false
    })
  },
  methods: {
    loadData () {
      this.isLoading = true
      const startDate = this.dateRange.startDate
      const endDate = this.dateRange.endDate

      if (startDate && endDate) {
        const start = format(startDate, 'YYYY-MM-DD')
        const end = format(endDate, 'YYYY-MM-DD')
        getPvStatus(start, end).then(data => {
          this.pvStatus = data
          this.isLoading = false
        }).catch(() => {
          this.pvStatus = []
          this.isLoading = false
        })
      } else {
        this.pvStatus = []
        this.isLoading = false
      }
    },
    togglePickerClick () {
      EventBus.$emit('closeAllUiSelectBox')
      EventBus.$emit('closeUserMenu')
      EventBus.$emit('closeReferrerList')
      EventBus.$emit('closeReportPageList')
      this.showDateRangePicker = !this.showDateRangePicker
    },
    closePicker () {
      this.showDateRangePicker = false
    },
    onRangeCancel () {
      this.closePicker()
    },
    onRangeChange (data) {
      this.dateRange = data.range
      this.loadData()
      this.closePicker()
    },
    onPagingOptionChange (page, limit) {
      this.page = page
      this.limit = limit
    },
    onDurationChange (duration) {
      const today = startOfDay(new Date())
      const minDate = startOfDay(parse(this.userData.regDate))
      const endDate = parse(today)
      let startDate = addMonths(today, -duration)
      if (duration === 0) {
        startDate = parse(this.userData.regDate)
      }

      if (startDate < minDate) {
        startDate = parse(minDate)
      }

      this.duration = duration

      this.onRangeChange({
        range: {
          startDate,
          endDate
        }
      })
      this.loadData()
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.remain-pv--wrap,
.remain-pv-table--wrap {
  padding: 15px 25px 30px 25px;
  box-sizing: border-box;
  margin-top: 30px;
}

.remain-pv--wrap {
  .content__title {
    font-size: 15px;
    font-weight: 600;
    color: #2f2f2f;
    display: inline-block;
    padding: 8px 15px 20px 0;
  }

  .content__label {
    color: #444444;
    font-size: 14px;
    margin-left: 20px;

    .icon {
      vertical-align: middle;
      transform: translateY(-1px);
      margin-right: 3px;
    }
  }

  .no-data {
    height: 100px;
    position: relative;
    &> p {
      text-align: center;
      @include positionYcenter;
    }
  }

  .date-range-picker--wrap {
    position: relative;
    float: right;
    display: inline-block;
    margin-left: 10px;
    button {
      position: relative;
      border-radius: 8px;
      border: solid 1px #e0e0e0;
      background-color: #ffffff;
      padding: 10px 34px 10px 10px;
      font-size: 13px;
      line-height: 1;
      color: #767676;
    }
    .icon--calendar {
      position: absolute;
      top: 50%;
      right: 8px;
      transform: translateY(-50%);
      pointer-events: none;
    }
    /deep/ .date_range_picker {
      top: 50px;
      left: -60px;
    }
  }
}

.remain-pv-table--wrap {
  .content {
    text-align: center;
  }
  .content__title {
    font-size: 15px;
    font-weight: 600;
    color: #2f2f2f;
    display: inline-block;
    padding: 11px 0 20px 0;
  }

  .ui_select {
    vertical-align: middle;
    transform: translateY(-2px);
    margin-left: 10px;
    position: relative;
    z-index: 2;
  }

  .page__comm--wrap {
    float: right;
    width: auto;
    margin-bottom: 15px;
  }

  .table--wrap {
    tbody tr {
      border-bottom: 1px solid #ebebeb;
      text-align: center;

      td {
        height: 50px;
        font-size: 13px;
        line-height: 1.23;
        color: #2f2f2f;
      }
    }
  }

  .no-data {
    height: 100px;
    padding-top: 15px;
    position: relative;
    &> p {
      text-align: center;
    }
  }
}

.calendar-enter-active,
.calendar-leave-active {
  transform-origin: top;
  transition: transform 0.3s ease,
              opacity 0.3s ease,
              height 0.3s ease !important;
}

.calendar-enter,
.calendar-leave-to{
  transform: translate(-50%, -30px) scaleY(0.5) !important;
  opacity: 0;
}
</style>
